import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Statistic, Table, Button, DatePicker, Segmented, Space,
  Typography, Spin, message, Tag, Select, Input,
} from 'antd';
import {
  DollarOutlined, ApiOutlined, DownloadOutlined, ReloadOutlined,
  CalendarOutlined, SearchOutlined, FilterOutlined,
} from '@ant-design/icons';
import { agentAPI, billingExportUrl } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export default function BillingDetails() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [models, setModels] = useState([]);
  const [modelFilter, setModelFilter] = useState('');
  const [keyword, setKeyword] = useState('');
  const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs()]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 30 });

  const loadModels = useCallback(async () => {
    try {
      const res = await agentAPI.getModelList();
      setModels((res.data?.models) || []);
    } catch (e) { console.error(e); }
  }, []);

  const loadData = useCallback(async () => {
    if (!dateRange[0] || !dateRange[1]) return;
    setLoading(true);
    try {
      const res = await agentAPI.getBilling({
        start_date: dateRange[0].format('YYYY-MM-DD'),
        end_date: dateRange[1].format('YYYY-MM-DD'),
        model: modelFilter || undefined,
        keyword: keyword || undefined,
        page: pagination.page,
        page_size: pagination.pageSize,
      });
      setRows(res.data?.list || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  }, [dateRange, modelFilter, keyword, pagination]);

  useEffect(() => { loadModels(); }, []);
  useEffect(() => { loadData(); }, [loadData]);

  const handlePreset = (key) => {
    const now = dayjs();
    const presets = {
      today: [now.startOf('day'), now],
      week: [now.startOf('week'), now],
      month: [now.startOf('month'), now],
      last30: [now.subtract(30, 'day'), now],
    };
    if (presets[key]) {
      setDateRange(presets[key]);
      setPagination(p => ({ ...p, page: 1 }));
    }
  };

  const handleExport = () => {
    const url = billingExportUrl({
      start_date: dateRange[0].format('YYYY-MM-DD'),
      end_date: dateRange[1].format('YYYY-MM-DD'),
      model: modelFilter || '',
      keyword: keyword || '',
    });
    window.open(url, '_blank');
  };

  // 汇总
  const totalCost = rows.reduce((s, r) => s + parseFloat(r.total_cost || 0), 0);
  const totalTokens = rows.reduce((s, r) => s + (r.input_tokens || 0) + (r.output_tokens || 0), 0);

  const columns = [
    {
      title: '时间', dataIndex: 'created_at', key: 'created_at', width: 170,
      render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
    },
    {
      title: '用户', dataIndex: 'user_email', key: 'user_email', width: 220,
      render: (t) => <Text>{t}</Text>,
    },
    {
      title: '模型', dataIndex: 'model', key: 'model', width: 170,
      render: (t) => <Tag color="geekblue">{t}</Tag>,
    },
    {
      title: '输入Token', dataIndex: 'input_tokens', key: 'input_tokens', align: 'right', width: 110,
      render: (v) => (v || 0).toLocaleString(),
    },
    {
      title: '输出Token', dataIndex: 'output_tokens', key: 'output_tokens', align: 'right', width: 110,
      render: (v) => (v || 0).toLocaleString(),
    },
    {
      title: '消费金额', dataIndex: 'total_cost', key: 'total_cost', align: 'right', width: 120,
      render: (v) => <Text strong style={{ color: '#f5576c' }}>¥{parseFloat(v || 0).toFixed(6)}</Text>,
      sorter: (a, b) => (a.total_cost || 0) - (b.total_cost || 0),
    },
  ];

  return (
    <div>
      {/* 筛选器 */}
      <Card bordered={false} style={{ borderRadius: 14, marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col>
            <Space size="middle" wrap>
              <CalendarOutlined style={{ fontSize: 18, color: '#667eea' }} />
              <Segmented
                options={[
                  { label: '今日', value: 'today' },
                  { label: '本周', value: 'week' },
                  { label: '本月', value: 'month' },
                  { label: '近30天', value: 'last30' },
                ]}
                onChange={handlePreset}
                defaultValue="month"
              />
              <RangePicker
                value={dateRange}
                onChange={(d) => { if (d) { setDateRange(d); setPagination(p => ({ ...p, page: 1 })); } }}
                allowClear={false}
              />
              <Select
                placeholder="筛选模型"
                allowClear
                style={{ width: 180 }}
                value={modelFilter || undefined}
                onChange={(v) => { setModelFilter(v || ''); setPagination(p => ({ ...p, page: 1 })); }}
                options={models.map(m => ({ label: m, value: m }))}
              />
              <Input.Search
                placeholder="搜索用户邮箱"
                allowClear
                style={{ width: 220 }}
                onSearch={(v) => { setKeyword(v || ''); setPagination(p => ({ ...p, page: 1 })); }}
              />
            </Space>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>刷新</Button>
              <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport}
                style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', border: 'none' }}>
                导出CSV
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* KPI 卡片 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16, marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card bordered={false} className="kpi-card" style={{ borderRadius: 14, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <Statistic
              title={<Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>当前页消费合计</Text>}
              value={totalCost} precision={4} prefix={<DollarOutlined />} suffix="元"
              valueStyle={{ color: '#fff', fontSize: 28, fontWeight: 700 }} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered={false} className="kpi-card" style={{ borderRadius: 14, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <Statistic
              title={<Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>总调用次数</Text>}
              value={total} prefix={<ApiOutlined />}
              valueStyle={{ color: '#fff', fontSize: 28, fontWeight: 700 }} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bordered={false} className="kpi-card" style={{ borderRadius: 14, background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
            <Statistic
              title={<Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>当前页 Token 总计</Text>}
              value={totalTokens}
              valueStyle={{ color: '#fff', fontSize: 28, fontWeight: 700 }} />
          </Card>
        </Col>
      </Row>

      {/* 明细表 */}
      <Card bordered={false} style={{ borderRadius: 14 }}
        title={<Title level={5} style={{ margin: 0 }}>流水明细</Title>}>
        <Table
          columns={columns}
          dataSource={rows}
          rowKey="id"
          loading={loading}
          size="small"
          scroll={{ x: 1000 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['20', '30', '50', '100', '200'],
            showTotal: (t) => `共 ${t} 条记录`,
            onChange: (page, pageSize) => setPagination({ page, pageSize }),
          }}
        />
      </Card>
    </div>
  );
}
